import { db } from '../firebase/config';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import type { PushSubscriptionPayload } from '../notifications/pushClient';

/**
 * Push subscriptions, one document per device per person.
 *
 * The endpoint is the device's address on its push service, so it is the natural
 * identity - but it is a long URL and cannot be a document id. Stored as a field
 * and looked up instead, which keeps one document per device rather than a new
 * one on every visit.
 *
 * Nothing reads these yet. They exist so that the day a sender is written, the
 * addresses are already there rather than needing everyone to re-enable.
 */
export async function saveSubscription(
  userId: string,
  subscription: PushSubscriptionPayload,
): Promise<void> {
  const existing = await getDocs(
    query(collection(db, 'pushSubscriptions'), where('endpoint', '==', subscription.endpoint)),
  );

  if (!existing.empty) {
    // Same device, possibly a different person or a rotated key.
    await updateDoc(doc(db, 'pushSubscriptions', existing.docs[0].id), {
      userId,
      p256dh: subscription.p256dh,
      auth: subscription.auth,
      updatedAt: serverTimestamp(),
    });
    return;
  }

  await addDoc(collection(db, 'pushSubscriptions'), {
    userId,
    endpoint: subscription.endpoint,
    p256dh: subscription.p256dh,
    auth: subscription.auth,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/** Forgets a device, by the endpoint it used to be reachable at. */
export async function deleteSubscription(endpoint: string): Promise<void> {
  const existing = await getDocs(
    query(collection(db, 'pushSubscriptions'), where('endpoint', '==', endpoint)),
  );
  await Promise.all(
    existing.docs.map((subscription) => deleteDoc(doc(db, 'pushSubscriptions', subscription.id))),
  );
}
